import {request} from './apiClient';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  limit,
  onSnapshot,
  getCountFromServer,
} from 'firebase/firestore';
import {db} from '../firebase';
import {
  buildPatientSearchQueries,
  normalizePatientSearchText,
} from '../utils/patientSearch';
import {normalizePatientNameParts} from '../utils/patientNames';
import {normalizePatientMrn} from '../utils/patientMrn';
import {logger, LogFlow, describeError} from './logger';
import {analytics} from './analytics';

// Scoped logger for the EHR patient-search hot paths. We log query LENGTHS and
// result COUNTS — never the query text or patient demographics (PHI).
const log = logger.scope(LogFlow.PatientSearch);

// Scoped logger for EHR write/config errors.
const ehrLog = logger.scope(LogFlow.Ehr);

// Report EHR write failures that the clinician sees as a push-failure banner
// (config/connection/server errors). The backend now logs the handled
// `EhrConfigError` (503/404) and reports it to Sentry, but that only fires when
// the request reaches the route handler — this captures the client's view so we
// still have observability if the banner shows but the backend report is missed,
// and gives us user/session correlation in PostHog. PHI-safe: status + route
// only, never the response body (which is note/patient-free config text but we
// keep the sink strict).
function reportEhrWriteError(status: number, route: string): void {
  if (!(status >= 500 || status === 403 || status === 404)) return;
  analytics.capture('ehr_config_error', {
    status_code: status,
    route,
    surface: 'web',
  });
  ehrLog.warn('ehr_config_error', {status_code: status, route});
}

// ── Cancelled / no-show encounter filtering ──────────────────────
//
// eCW reports a visit-status code on each encounter (cached as
// `EhrCache/{companyId}/encounters/{id}.status`). Cancelled and no-show
// visits still come back from `fetchPatientEncounters`, so without this
// guard they show up in the "push note to encounter" picker — and a note
// pushed to a cancelled visit can't be edited in eCW afterwards. We hide
// them so a clinician can't accidentally route a note to a dead visit.
//
// Observed real eCW codes: `CAN`/`CANC` (cancelled), `N/S` (no-show).
// We normalize (uppercase, strip non-alphanumerics) and also accept the
// human-readable forms + `RESCHEDULED`, mirroring the app's existing
// appointment-status ignore list (see utils/appointmentStatus.ts). Codes
// like `PEN`, `CHK`, `CONFPHONE`, etc. are kept.
const IGNORED_ENCOUNTER_STATUSES = new Set([
  'CAN',
  'CANC',
  'CANCEL',
  'CANCELED',
  'CANCELLED',
  'NS', // eCW "N/S"
  'NOSHOW',
  'RESCHEDULED',
  'RESCH',
]);

export function isCancelledEncounterStatus(status: unknown): boolean {
  if (status == null) return false;
  const normalized = String(status)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  if (!normalized) return false;
  return (
    IGNORED_ENCOUNTER_STATUSES.has(normalized) ||
    normalized.startsWith('CANCEL')
  );
}

// ── Client-side encounter-sync dedup ─────────────────────────────
//
// The backend already de-duplicates concurrent `fetch-encounters` runs
// via a Firestore transaction (see `EhrSyncService.syncEncountersForPatient`)
// — this map is purely a network-traffic optimization. It avoids firing
// HTTP requests we already know the backend will skip, e.g. when a user
// bounces between the patient list and the patient detail panel a few
// times in a row, or when the same tab opens scribe → ends → opens
// scribe again on the same patient.
//
// The window matches the backend's `ENCOUNTER_SYNC_SUCCESS_FRESHNESS_MS`
// (5 minutes); any longer and we'd hide legitimate refreshes, any shorter
// and the per-tab churn isn't actually deduped. Stored at module scope so
// it survives component remounts within the same SPA load — page reload
// is fine to refire because by then the in-memory cache is gone anyway.
const ENCOUNTER_SYNC_DEDUP_MS = 5 * 60 * 1000;
const lastEncounterSyncByPatientId = new Map();

const EHR_PREFIX = '/api/v1/ehr';

// ── EHR Backend API Client ──────────────────────────────────────
// Ported from iOS EHRService.swift — single source of truth for
// all EHR-related backend and Firestore cache interactions.
//
// Both helpers route through apiClient.request but keep the EHR-specific
// response/error semantics: raw `{ success, data, ... }` envelopes (callers
// check `.success` themselves), status-tagged errors, passthrough statuses,
// and ehrPost's acceptedStatuses contract.

// Tag thrown errors with the HTTP status so callers (e.g. the deep-search
// banner) can distinguish a transient backend/EHR-integration outage
// (5xx / 429) from a real config error and show the right copy.
const withStatus = (message: any, status: any) =>
  Object.assign(new Error(message), {status});

// Chart-panel reads are live eCW browser-automation round-trips, and the
// COLD ones are slow: prod logs show problem list 56–119s, prior notes
// 118–137s, encounter sync 160s (warm repeats run sub-second once the
// Zatanna session is up). All of those blow past the apiClient's 45s
// default abort — the fetch would "succeed" server-side after the browser
// had already hung up. Three minutes clears every cold load we've
// observed while still rescuing a genuinely hung socket.
const EHR_CHART_READ_TIMEOUT_MS = 180_000;

// Shared status-tagged error mapping for EHR *read* calls (GET and the
// read-style POSTs like `/notes/fetch`). 403/429 get stable copy the
// banners key on; other ApiErrors keep their status so callers (e.g. the
// chart panel's per-tab error states) can branch on it.
function throwMappedEhrError(err: any): never {
  const status = err?.status ?? null;
  if (status === 403)
    throw withStatus('EHR is not configured for your organization.', 403);
  if (status === 429)
    throw withStatus(
      'Too many requests. Please wait before trying again.',
      429,
    );
  // The apiClient's timeout abort (status null, "Request timed out…").
  // Tag it 408 so the chart panel can say "eCW is answering slowly"
  // instead of a generic failure — the read usually completes upstream
  // moments later and a retry hits the warmed session.
  if (err?.name === 'ApiError' && /timed out/i.test(err?.message || '')) {
    throw withStatus('The EHR is answering slowly. Please try again.', 408);
  }
  if (err?.name === 'ApiError') {
    const body =
      err.payload &&
      typeof err.payload === 'object' &&
      Object.keys(err.payload).length
        ? JSON.stringify(err.payload)
        : typeof err.payload === 'string'
          ? err.payload
          : '';
    throw withStatus(
      `EHR request failed (${status}): ${body || 'Unknown error'}`,
      status,
    );
  }
  throw err; // network error / abort
}

async function ehrGet(
  path: any,
  queryParams: Record<string, any> = {},
  options: Record<string, any> = {},
) {
  try {
    return await request(`${EHR_PREFIX}${path}`, {
      method: 'GET',
      query: queryParams,
      headers: {Accept: 'application/json'},
      signal: options.signal,
      passthroughStatuses: options.passthroughStatuses,
      // Callers doing legitimately slow reads (live eCW round-trips)
      // override the 45s apiClient default here.
      ...(options.timeoutMs ? {timeoutMs: options.timeoutMs} : {}),
      unwrap: false,
      successCheck: false,
    });
  } catch (err) {
    throwMappedEhrError(err);
  }
}

// POST variant of `ehrGet` for read-style POST endpoints (today just
// `/notes/fetch`). Distinct from `ehrPost`, which has WRITE semantics
// (raw Response + acceptedStatuses contract, no status-tagged errors) —
// reads want the same 403/429/status mapping as `ehrGet` so the chart
// panel can render config/rate-limit states instead of a generic error.
async function ehrPostRead(
  path: any,
  body: any,
  options: Record<string, any> = {},
) {
  try {
    return await request(`${EHR_PREFIX}${path}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {Accept: 'application/json'},
      signal: options.signal,
      ...(options.timeoutMs ? {timeoutMs: options.timeoutMs} : {}),
      unwrap: false,
      successCheck: false,
    });
  } catch (err) {
    throwMappedEhrError(err);
  }
}

async function ehrPost(
  path: any,
  body: any,
  acceptedStatuses = [200, 201, 202, 207],
) {
  const res = await request(`${EHR_PREFIX}${path}`, {
    method: 'POST',
    body: JSON.stringify(body),
    parse: 'raw',
  });
  if (!acceptedStatuses.includes(res.status)) {
    const text = await res.text().catch(() => '');
    reportEhrWriteError(res.status, path);
    throw new Error(
      `EHR write failed (${res.status}): ${text || 'Unknown error'}`,
    );
  }
  return res.json();
}

// ── Patient chart panel row types ────────────────────────────────
//
// Display shapes returned by the chart-panel read wrappers below. They
// mirror the backend's Zatanna value types (minus the vendor `raw`
// blobs, which the backend strips). Everything optional: the vendor
// fills fields opportunistically and the panel renders what's present.

export interface EhrChartProblem {
  code?: string;
  diagnosis?: string;
  /** MM/DD/YYYY the problem was added to the chart. */
  dateAdded?: string;
  icdVersion?: string;
  status?: string;
}

export interface EhrChartMedication {
  itemName?: string;
  medName?: string;
  strength?: string;
  formulation?: string;
  dispense?: string;
  sig?: string;
  frequency?: string;
  duration?: string;
  refills?: string;
  route?: string;
  status?: string;
  rxChannel?: string;
  ndcCode?: string;
  pharmacy?: string;
  diagnosis?: string;
  prescriber?: string;
  genericName?: string;
  encounterDate?: string;
  startDate?: string;
  rxOrderNo?: string;
  csaSchedule?: string;
  isCompound?: boolean;
  notes?: string;
}

export interface EhrChartMedicationGroup {
  heading?: string;
  totalRecords?: number;
  medications: EhrChartMedication[];
}

export interface EhrChartAllergy {
  id?: string;
  name?: string;
  reaction?: string;
  onsetDate?: string;
  criticality?: string;
  type?: string;
}

export interface EhrChartImmunizationDose {
  id?: string;
  name?: string;
  givenDate?: string;
  dose?: string;
  doseNumber?: string;
  route?: string;
  location?: string;
  lotNumber?: string;
  status?: string;
  givenBy?: string;
  impression?: string;
}

export interface EhrChartImmunization {
  name?: string;
  targetId?: string;
  doses?: EhrChartImmunizationDose[];
}

/** One Labs/Imaging/Procedures row from the eCW review worklist. */
export interface EhrChartOrder {
  reportId?: string;
  itemId?: string;
  encounterId?: string;
  patientId?: string;
  name?: string;
  orderDate?: string;
  collectionDate?: string;
  resultDate?: string;
  result?: string;
  assignedTo?: string;
  providerId?: string;
  reportType?: string;
  received?: string;
  status?: string;
  overdue?: string;
}

export interface EhrProgressNoteSection {
  heading?: string;
  body?: string;
}

export interface EhrProgressNote {
  encounter?: {
    encounterId?: string;
    patientId?: string;
    providerId?: string;
    date?: string;
    startTime?: string;
    visitType?: string;
    visitTypeDescription?: string;
    reason?: string;
  };
  patient?: {
    patientId?: string;
    firstName?: string;
    lastName?: string;
    preferredName?: string;
    dateOfBirth?: string;
    sex?: string;
  };
  sections?: EhrProgressNoteSection[];
  /** Raw eCW page markup — do NOT render; use `sections` instead. */
  html?: string;
  /**
   * Set to 'telephone' when the backend recovered this content from the
   * telephone-encounter note (TEL visits render an empty progress-note
   * shell in eCW), so readers can label the note honestly.
   */
  noteType?: string;
}

// The EhrConfig doc changes only when the integration itself is reconfigured
// (server-side; nothing in the client writes it), yet the planner, pickers,
// and workflow pages each re-fetch it on mount — several identical Firestore
// reads per page visit, some gating first paint. A short TTL plus in-flight
// dedupe collapses them; `{fresh: true}` is the escape hatch for any surface
// that must re-read after a backend-side change.
const EHR_CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;
const ehrConfigCache = new Map<string, {value: any; expiresAt: number}>();
const ehrConfigPending = new Map<string, Promise<any>>();

export const clearEhrConfigCache = () => {
  ehrConfigCache.clear();
  ehrConfigPending.clear();
};

export const ehrService = {
  // ── EhrConfig (Firestore) ──────────────────────────────────
  async fetchEhrConfig(
    companyId: any,
    {fresh = false}: Record<string, any> = {},
  ) {
    if (!companyId) return null;
    const key = String(companyId);
    if (!fresh) {
      const cached = ehrConfigCache.get(key);
      if (cached && cached.expiresAt > Date.now()) return cached.value;
      const pending = ehrConfigPending.get(key);
      if (pending) return pending;
    }
    const request = (async () => {
      const ref = collection(db, 'EhrConfig');
      const q = query(ref, where('company_id', '==', companyId), limit(1));
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      const d = snapshot.docs[0];
      return {id: d.id, ...d.data()};
    })()
      .then(value => {
        ehrConfigCache.set(key, {
          value,
          expiresAt: Date.now() + EHR_CONFIG_CACHE_TTL_MS,
        });
        return value;
      })
      .finally(() => {
        ehrConfigPending.delete(key);
      });
    ehrConfigPending.set(key, request);
    return request;
  },

  isConfigActive(config: any) {
    return config?.status === 'active';
  },

  /**
   * Resolved IANA timezone for the practice — never null. Ayden/ECW
   * appointments are wall-clock strings in facility-local time, so every
   * "now / soon / past" decision needs this. Legacy configs that don't
   * have `timeZone` set fall back to Eastern because our current practice
   * (CIFC) is in Connecticut. See `EhrConfig.timeZone` in the backend.
   */
  resolveTimeZone(config: any) {
    const tz = config?.timeZone;
    if (typeof tz === 'string' && tz.trim().length > 0) return tz.trim();
    return 'America/New_York';
  },

  // ── EHR Patients (Backend API) ─────────────────────────────
  async fetchPatients(
    searchQuery: any = null,
    pageLimit: number = 50,
    cursor: any = null,
  ) {
    const params: Record<string, any> = {limit: String(pageLimit)};
    if (searchQuery) params.q = searchQuery;
    if (cursor) params.cursor = cursor;
    const startedAt = Date.now();
    let response;
    try {
      response = await ehrGet('/patients', params);
    } catch (error) {
      log.error('fetch_patients_failed', {
        query_length: searchQuery ? String(searchQuery).length : 0,
        page_limit: pageLimit,
        paged: Boolean(cursor),
        duration_ms: Date.now() - startedAt,
        ...describeError(error),
      });
      throw error;
    }
    if (!response.success) {
      log.error('fetch_patients_failed', {
        query_length: searchQuery ? String(searchQuery).length : 0,
        page_limit: pageLimit,
        duration_ms: Date.now() - startedAt,
        error_message: response.error || 'unknown',
      });
      throw new Error(response.error || 'Failed to fetch EHR patients');
    }
    const patients = (response.data || []).map((p: any) =>
      mapEHRPatient(p.id, p),
    );
    log.info('fetch_patients_succeeded', {
      query_length: searchQuery ? String(searchQuery).length : 0,
      page_limit: pageLimit,
      results_count: patients.length,
      has_next_page: Boolean(response.next_cursor),
      duration_ms: Date.now() - startedAt,
    });
    return {
      patients,
      nextCursor: response.next_cursor || null,
    };
  },

  async fetchPatient(id: any) {
    const response = await ehrGet(`/patients/${id}`);
    if (!response.success)
      throw new Error(response.error || 'Failed to fetch EHR patient');
    return response.data;
  },

  // ── Deep (live) EHR patient search via Ayden pool ──────────
  // Returns an object with either { patients, durationMs } on success,
  // { outsideHours: true } when the pool window is closed (503), or
  // throws for unexpected errors. Accepts an optional AbortSignal.
  async deepSearchPatients(
    query: any,
    pageLimit: number = 20,
    {signal}: Record<string, any> = {},
  ) {
    const trimmed = (query || '').trim();
    if (trimmed.length < 3) {
      return {patients: [], durationMs: 0};
    }
    const startedAt = Date.now();
    log.info('deep_search_dispatched', {
      query_length: trimmed.length,
      page_limit: pageLimit,
    });
    let response;
    try {
      response = await ehrGet(
        '/patients/live-search',
        {q: trimmed, limit: String(pageLimit)},
        {signal, passthroughStatuses: [503]},
      );
    } catch (error) {
      // AbortError is an expected supersession/timeout signal, not a failure
      // worth an error-level log; the hook already attributes those.
      const aborted = (error as any)?.name === 'AbortError';
      log[aborted ? 'info' : 'error']('deep_search_request_threw', {
        query_length: trimmed.length,
        aborted,
        duration_ms: Date.now() - startedAt,
        ...describeError(error),
      });
      throw error;
    }
    if (response?.__passthroughStatus === 503) {
      log.warn('deep_search_outside_hours', {
        query_length: trimmed.length,
        duration_ms: Date.now() - startedAt,
      });
      return {patients: [], durationMs: 0, outsideHours: true};
    }
    if (!response.success) {
      log.error('deep_search_failed', {
        query_length: trimmed.length,
        duration_ms: Date.now() - startedAt,
        error_message: response.error || 'unknown',
      });
      throw new Error(response.error || 'Deep EHR search failed');
    }
    const patients = (response.data || []).map((p: any) =>
      mapEHRPatient(p.id, p),
    );
    log.info('deep_search_succeeded', {
      query_length: trimmed.length,
      results_count: patients.length,
      backend_duration_ms: response.duration_ms || 0,
      duration_ms: Date.now() - startedAt,
    });
    return {
      patients,
      durationMs: response.duration_ms || 0,
    };
  },

  // ── EHR Patients (Firestore Cache) ─────────────────────────
  async fetchCachedPatients(companyId: any, searchQuery: any = null) {
    if (!companyId) return [];
    const patientsRef = collection(db, 'EhrCache', companyId, 'patients');
    let snapshot;
    const startedAt = Date.now();
    const queryLength =
      searchQuery && searchQuery.trim() ? String(searchQuery).trim().length : 0;

    if (searchQuery && searchQuery.trim()) {
      const searchValues = buildCachedPatientSearchValues(searchQuery);
      const snapshots: any[] = [];
      let usedFallback = false;
      try {
        const tasks = [];

        for (const prefix of searchValues.namePrefixes.slice(0, 6)) {
          tasks.push(
            getDocs(
              query(
                patientsRef,
                where('lastName', '>=', prefix),
                where('lastName', '<=', `${prefix}\uf8ff`),
                limit(50),
              ),
            ),
          );
        }

        for (const field of ['dob', 'mrn', 'accountNo', 'ehr_source_id']) {
          for (const chunk of chunkArray(searchValues.exactValues, 30)) {
            tasks.push(
              getDocs(query(patientsRef, where(field, 'in', chunk), limit(50))),
            );
          }
        }

        const settled = await Promise.allSettled(tasks);
        settled.forEach(result => {
          if (result.status === 'fulfilled') snapshots.push(result.value);
        });
      } catch {
        usedFallback = true;
        snapshots.push(await getDocs(query(patientsRef, limit(100))));
      }

      const docsById = new Map();
      snapshots.forEach(snap => {
        snap.docs.forEach((d: any) => docsById.set(d.id, d));
      });
      const results = [...docsById.values()].map(d =>
        mapEHRPatient(d.id, d.data()),
      );
      log.info('cached_search_completed', {
        query_length: queryLength,
        results_count: results.length,
        used_fallback: usedFallback,
        duration_ms: Date.now() - startedAt,
      });
      return results;
    } else {
      snapshot = await getDocs(query(patientsRef, limit(200)));
    }

    const browseResults = snapshot.docs.map(d => mapEHRPatient(d.id, d.data()));
    log.info('cached_roster_loaded', {
      results_count: browseResults.length,
      duration_ms: Date.now() - startedAt,
    });
    return browseResults;
  },

  async fetchCachedPatientById(companyId: any, patientId: any) {
    if (!companyId || !patientId) return null;
    const d = await getDoc(
      doc(db, 'EhrCache', companyId, 'patients', patientId),
    );
    if (!d.exists()) return null;
    return mapEHRPatient(d.id, d.data());
  },

  // Approximate total cached EHR patient count for a company, surfaced in
  // the picker hint copy ("your roster has N patients"). `getCountFromServer`
  // is billed at ~1/100 of a normal read so calling this once per modal
  // open is cheap. Returns null on failure so callers can hide the hint
  // rather than render a misleading number.
  async getCachedPatientCount(companyId: any) {
    if (!companyId) return null;
    try {
      const snap = await getCountFromServer(
        collection(db, 'EhrCache', companyId, 'patients'),
      );
      return snap.data().count;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('getCachedPatientCount failed:', err?.message);
      }
      return null;
    }
  },

  async fetchCachedPatientsByIds(companyId: any, patientIds: any) {
    if (!companyId || !patientIds?.length) return {};
    const result: Record<string, any> = {};
    const patientsRef = collection(db, 'EhrCache', companyId, 'patients');

    const byDocId = await Promise.allSettled(
      patientIds.map(async (pid: any) => {
        const d = await getDoc(
          doc(patientsRef.firestore, `EhrCache/${companyId}/patients/${pid}`),
        );
        if (d.exists()) return {pid, patient: mapEHRPatient(d.id, d.data())};
        return {pid, patient: null};
      }),
    );

    const missing: any[] = [];
    for (const r of byDocId) {
      if (r.status === 'fulfilled' && r.value.patient) {
        result[r.value.pid] = r.value.patient;
      } else if (r.status === 'fulfilled') {
        missing.push(r.value.pid);
      }
    }

    if (missing.length > 0) {
      const chunks = chunkArray(missing, 30);
      const chunkSnaps = await Promise.allSettled(
        chunks.map(chunk =>
          getDocs(query(patientsRef, where('ehr_source_id', 'in', chunk))),
        ),
      );
      for (const settled of chunkSnaps) {
        if (settled.status !== 'fulfilled') continue; // ignore chunk failures
        for (const d of settled.value.docs) {
          const sourceId = d.data().ehr_source_id || d.id;
          if (missing.includes(sourceId)) {
            result[sourceId] = mapEHRPatient(d.id, d.data());
          }
        }
      }
    }

    return result;
  },

  // Resolve full demographics for EHR patients by source id, used to hydrate
  // thin live-search hits (name only) into the same shape cached/encounter
  // patients carry. Cache first — cheap and covers synced patients — then fall
  // back to a live per-patient detail fetch for brand-new patients that aren't
  // in the cache yet. The live fallback is capped so a search can't fan out
  // into an unbounded number of EHR detail calls. Returns { [sourceId]: patient }.
  async resolveEhrPatients(
    companyId: any,
    ids: any,
    // Cap matches the planner search page size (SEARCH_RESULT_LIMIT = 12) so a
    // full page of uncached hits can each get a live detail fetch; a smaller
    // cap silently left the tail of the page un-hydrated (blank demographics).
    {liveFallback = true, liveFallbackLimit = 12}: Record<string, any> = {},
  ) {
    const uniqueIds = [...new Set((ids || []).filter(Boolean))];
    if (!uniqueIds.length) return {};

    const resolved: Record<string, any> = companyId
      ? await this.fetchCachedPatientsByIds(companyId, uniqueIds)
      : {};

    if (!liveFallback) return resolved;

    const missing = uniqueIds
      .filter((id: any) => !resolved[id])
      .slice(0, liveFallbackLimit);
    if (!missing.length) return resolved;

    const fetched = await Promise.allSettled(
      missing.map(async (id: any) => {
        const data = await this.fetchPatient(id);
        if (!data) return null;
        // Keep the raw payload alongside the normalized fields so downstream
        // alias resolution catches demographics regardless of the detail
        // endpoint's casing (dob / dateOfBirth / date_of_birth, etc.).
        return {
          id,
          patient: {
            ...data,
            ...mapEHRPatient(data.ehr_source_id || data.id || id, data),
          },
        };
      }),
    );
    for (const result of fetched) {
      if (result.status === 'fulfilled' && result.value) {
        resolved[result.value.id] = result.value.patient;
      }
    }
    return resolved;
  },

  // ── Appointments (Firestore Cache) ─────────────────────────
  async fetchCachedAppointments(companyId: any, dates: any = null) {
    if (!companyId) return [];
    const apptRef = collection(db, 'EhrCache', companyId, 'appointments');

    let snapshot;
    if (dates && dates.length > 0) {
      const sorted = [...dates].sort();
      const start = sorted[0];
      const endDate = new Date(sorted[sorted.length - 1]);
      endDate.setDate(endDate.getDate() + 1);
      const upperBound = endDate.toISOString().slice(0, 10);

      snapshot = await getDocs(
        query(
          apptRef,
          where('appointmentDate', '>=', start),
          where('appointmentDate', '<', upperBound),
        ),
      );
    } else {
      snapshot = await getDocs(query(apptRef, limit(200)));
    }

    return snapshot.docs
      .map(d => {
        const data = d.data();
        const rawDate = data.appointmentDate || '';
        let datePart = rawDate;
        let timePart = data.appointmentTime || '';
        if (rawDate.includes(' ')) {
          const idx = rawDate.indexOf(' ');
          datePart = rawDate.slice(0, idx);
          timePart = timePart || rawDate.slice(idx + 1);
        }
        // eCW books each visit under a billing provider (providerId) but also
        // records the rendering "resource(s)" — which is how residents/staff
        // who aren't the billing provider are attached to a slot. Surface the
        // resource id(s) so the planner can scope a resident's schedule to the
        // rows where they are the resource, not just the attending.
        //
        // The backend now parses these into a clean `resourceProviderIds`
        // array on the appointment doc; prefer it and fall back to parsing
        // the raw `resources` string for docs cached before that change.
        const resourceIds = Array.isArray(data.resourceProviderIds)
          ? data.resourceProviderIds
              .map((value: any) => String(value).trim())
              .filter(Boolean)
          : (() => {
              const rawResources =
                data.raw_data?.resources ?? data.resources ?? null;
              return rawResources == null
                ? []
                : String(rawResources)
                    .split(/[,;]/)
                    .map((value: string) => value.trim())
                    .filter(Boolean);
            })();
        return {
          id: d.id,
          patientId: data.patientId || null,
          providerId: data.providerId || null,
          providerName: data.providerName || null,
          resourceIds,
          patientName: data.patientName || data.patient_name || null,
          appointmentDate: datePart,
          appointmentTime: timePart,
          appointmentEnd: data.appointmentEnd || data.endTime || null,
          facility: data.facility || null,
          reason: data.reason || null,
          language: data.language || null,
          dialect: data.dialect || null,
          status: data.status || null,
        };
      })
      .sort((a, b) => {
        const keyA = (a.appointmentDate || '') + (a.appointmentTime || '');
        const keyB = (b.appointmentDate || '') + (b.appointmentTime || '');
        return keyA.localeCompare(keyB);
      });
  },

  // ── Encounters (Firestore Cache) ───────────────────────────
  async fetchCachedEncounters(companyId: any, patientId: any) {
    if (!companyId || !patientId) return [];
    const encRef = collection(db, 'EhrCache', companyId, 'encounters');
    const snapshot = await getDocs(
      query(encRef, where('patientId', '==', patientId)),
    );
    return (
      snapshot.docs
        .map(d => {
          const data = d.data();
          const rawData = data.raw_data || {};
          // `locked === true` means the encounter is signed/locked in the EHR
          // (Ayden's `encLock === '1'`) and cannot be written to. Missing /
          // undefined means the EHR didn't report a lock state — treated as
          // writable, matching backend behavior where `locked` is only set
          // when `encLock` is present.
          return {
            id: d.id,
            patientId: data.patientId || null,
            encounterId: data.encounterId || d.id,
            encounterDate: data.encounterDate || null,
            encounterTime: rawData.startTime || null,
            encounterReason: data.encounterReason || null,
            providerId: data.providerId || null,
            providerName: data.providerName || null,
            // eCW's visit-type code (TEL, F/U, ACUTE, …) — the chart panel
            // shows it on encounter rows so phone visits are recognizable.
            visitType: data.visitType || null,
            // eCW visit-status code (PEN, CHK, CAN, N/S, …); used to hide
            // cancelled / no-show visits from the push picker below.
            status: data.status || null,
            locked: data.locked === true,
          };
        })
        // Cancelled / no-show visits must never be selectable in the push
        // picker — notes routed to them can't be edited in eCW afterwards.
        .filter(enc => !isCancelledEncounterStatus(enc.status))
        .sort((a, b) => {
          const today = new Date();
          const dateA = a.encounterDate ? new Date(a.encounterDate) : null;
          const dateB = b.encounterDate ? new Date(b.encounterDate) : null;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return (
            Math.abs(dateA.getTime() - today.getTime()) -
            Math.abs(dateB.getTime() - today.getTime())
          );
        })
    );
  },

  // ── Patient chart panel reads (Backend API → live eCW) ─────
  //
  // JIT data for the chart context panel: each wrapper maps to one
  // backend route that proxies a single Zatanna read op. All are live
  // eCW round-trips — callers go through `patientChartCache` so tab
  // flips within its TTL never re-fire them. Errors carry `.status`
  // (403 not configured / 429 rate-limited / 503 unavailable) via
  // `throwMappedEhrError` so the panel can render friendly states.

  async fetchProblemList(
    patientId: any,
  ): Promise<{problems: EhrChartProblem[]}> {
    const res = await ehrGet(
      `/patients/${patientId}/problem-list`,
      {},
      {timeoutMs: EHR_CHART_READ_TIMEOUT_MS},
    );
    return {problems: res?.data?.problems || []};
  },

  async fetchMedications(patientId: any): Promise<{
    groups: EhrChartMedicationGroup[];
    medications: EhrChartMedication[];
  }> {
    const res = await ehrGet(
      `/patients/${patientId}/medications`,
      {},
      {timeoutMs: EHR_CHART_READ_TIMEOUT_MS},
    );
    return {
      groups: res?.data?.groups || [],
      medications: res?.data?.medications || [],
    };
  },

  // One backend call serves BOTH the Allergies and Immunizations tabs —
  // cache accordingly (key by the op, not the tab).
  async fetchAllergiesImmunizations(patientId: any): Promise<{
    allergies: EhrChartAllergy[];
    immunizations: EhrChartImmunization[];
  }> {
    const res = await ehrGet(
      `/patients/${patientId}/allergies-immunizations`,
      {},
      {timeoutMs: EHR_CHART_READ_TIMEOUT_MS},
    );
    return {
      allergies: res?.data?.allergies || [],
      immunizations: res?.data?.immunizations || [],
    };
  },

  // Labs/Imaging/Procedures from the eCW review worklist (the backend
  // filters the practice-wide worklist to this patient). Worklist-scoped,
  // not a complete historical archive — the tab says so in a caption.
  async fetchOutstandingOrders(
    patientId: any,
  ): Promise<{orders: EhrChartOrder[]}> {
    const res = await ehrGet(
      `/patients/${patientId}/orders`,
      {},
      {timeoutMs: EHR_CHART_READ_TIMEOUT_MS},
    );
    return {orders: res?.data?.orders || []};
  },

  // Full progress note for one prior encounter. Render `sections`; the
  // raw `html` is vendor page markup and stays unrendered.
  async fetchProgressNote(
    patientId: any,
    encounterId: any,
  ): Promise<EhrProgressNote | null> {
    const res = await ehrPostRead(
      '/notes/fetch',
      {patientId, encounterId},
      {timeoutMs: EHR_CHART_READ_TIMEOUT_MS},
    );
    return res?.data || null;
  },

  // ── Note Templates (Backend API) ───────────────────────────
  async fetchNoteTemplates() {
    const response = await ehrGet('/note-templates');
    return {
      success: response.success,
      ehrEnabled: response.ehr_enabled,
      ehrType: response.ehr_type || null,
      templates: (response.data || []).map(normalizeEHRTemplate),
    };
  },

  // ── Write Notes (Backend API) ──────────────────────────────
  async writeNoteSections(
    patientId: any,
    encounterId: any,
    sections: any,
    {
      templateId,
      templateName,
      conversationId,
      appendInput,
      importHistories,
      historyTypes,
    }: Record<string, any> = {},
  ) {
    return ehrPost(`/patients/${patientId}/notes`, {
      encounterId,
      sections,
      // Forward `appendInput` / `importHistories` / `historyTypes`
      // only when the caller actually picked a value — the backend
      // defaults to merge (true) and import-histories (true,
      // unfiltered) when omitted, so legacy callers that don't yet
      // expose the toggles keep their old behavior automatically.
      // `historyTypes` is the per-section picker (medical, surgical,
      // social, family, hospitalization); an empty array means "skip
      // every history" and is forwarded so the backend can no-op the
      // workflow cleanly.
      ...(typeof appendInput === 'boolean' ? {appendInput} : {}),
      ...(typeof importHistories === 'boolean' ? {importHistories} : {}),
      ...(Array.isArray(historyTypes) ? {historyTypes} : {}),
      ...(templateId ? {templateId} : {}),
      ...(templateName ? {templateName} : {}),
      ...(conversationId ? {conversationId} : {}),
    });
  },

  async writeWholeNote({
    patientId,
    encounterId,
    chiefComplaint,
    hpiData,
    hpiCategory,
    rosNotes,
    icd10,
    examinationData,
    treatmentNotes,
    importHistories,
    historyTypes,
    appendInput,
    templateId,
    templateName,
    conversationId,
  }: Record<string, any>) {
    const normalizedIcd10 = normalizeIcd10Payload(icd10);
    return ehrPost('/notes/write', {
      patientId,
      encounterId,
      chiefComplaint,
      hpiData,
      ...(hpiCategory ? {hpiCategory} : {}),
      rosNotes,
      icd10: normalizedIcd10,
      examinationData,
      treatmentNotes,
      importHistories,
      ...(Array.isArray(historyTypes) ? {historyTypes} : {}),
      ...(typeof appendInput === 'boolean' ? {appendInput} : {}),
      ...(templateId ? {templateId} : {}),
      ...(templateName ? {templateName} : {}),
      ...(conversationId ? {conversationId} : {}),
    });
  },

  // ── Per-section writes (synchronous backend endpoints) ─────────
  // Used by the push view to write each section independently and mark
  // it done/failed the moment ITS OWN request resolves — real per-
  // section progress instead of a scripted animation. Each call awaits
  // its single section write on the backend and returns
  // `{ success, data }` (or throws on a non-2xx, which the caller maps
  // to a failed section).
  async writeChiefComplaint({
    patientId,
    encounterId,
    chiefComplaint,
    appendInput,
  }: Record<string, any>) {
    return ehrPost('/notes/write-cc', {
      patientId,
      encounterId,
      chiefComplaint,
      ...(typeof appendInput === 'boolean' ? {appendInput} : {}),
    });
  },

  async writeHpiSection({
    patientId,
    encounterId,
    hpiData,
    hpiCategory,
    appendInput,
  }: Record<string, any>) {
    return ehrPost('/notes/write-hpi', {
      patientId,
      encounterId,
      hpiData,
      ...(hpiCategory ? {category: hpiCategory} : {}),
      ...(typeof appendInput === 'boolean' ? {appendInput} : {}),
    });
  },

  async writeRosSection({
    patientId,
    encounterId,
    rosNotes,
    appendInput,
  }: Record<string, any>) {
    return ehrPost('/notes/write-ros', {
      patientId,
      encounterId,
      rosNotes,
      ...(typeof appendInput === 'boolean' ? {appendInput} : {}),
    });
  },

  async writePreventiveMedicineSection({
    patientId,
    encounterId,
    preventiveMedicineData,
    appendInput,
  }: Record<string, any>) {
    return ehrPost('/notes/write-preventive-medicine', {
      patientId,
      encounterId,
      preventiveMedicineData,
      ...(typeof appendInput === 'boolean' ? {appendInput} : {}),
    });
  },

  async writeExaminationSection({
    patientId,
    encounterId,
    examinationData,
    appendInput,
  }: Record<string, any>) {
    return ehrPost('/notes/write-examination', {
      patientId,
      encounterId,
      examinationData,
      ...(typeof appendInput === 'boolean' ? {appendInput} : {}),
    });
  },

  // Resolves to `{ success, data: { icd10CodeMap, icd10OptionsSent } }`.
  // The returned `icd10CodeMap` MUST be forwarded to `writeTreatment` so
  // post-coordinated codes the EHR renumbered still get their plan.
  async writeAssessments({
    patientId,
    encounterId,
    icd10,
    appendInput,
    conversationId,
    assessmentText,
    planText,
  }: Record<string, any>) {
    return ehrPost('/notes/write-assessments', {
      patientId,
      encounterId,
      icd10: normalizeIcd10Payload(icd10),
      ...(typeof appendInput === 'boolean' ? {appendInput} : {}),
      ...(conversationId ? {conversationId} : {}),
      ...(assessmentText ? {assessmentText} : {}),
      ...(planText ? {planText} : {}),
    });
  },

  async importHistories({
    patientId,
    encounterId,
    appendInput,
    historyTypes,
  }: Record<string, any>) {
    return ehrPost('/notes/import-histories', {
      patientId,
      encounterId,
      ...(typeof appendInput === 'boolean' ? {appendInput} : {}),
      ...(Array.isArray(historyTypes) ? {historyTypes} : {}),
    });
  },

  // Resolves to `{ success, data: { results: [{ icd10Code, success, error? }], warning? } }`.
  // Pass the active `icd10` codes + the `icd10CodeMap` from the
  // assessments response so treatment gates on what was written and
  // targets the EHR-final codes.
  async writeTreatment({
    patientId,
    encounterId,
    treatmentNotes,
    appendInput,
    icd10,
    icd10CodeMap,
  }: Record<string, any>) {
    return ehrPost('/notes/write-treatment', {
      patientId,
      encounterId,
      treatmentNotes,
      ...(typeof appendInput === 'boolean' ? {appendInput} : {}),
      ...(Array.isArray(icd10) && icd10.length > 0
        ? {icd10: normalizeIcd10Payload(icd10)}
        : {}),
      ...(icd10CodeMap && Object.keys(icd10CodeMap).length > 0
        ? {icd10CodeMap}
        : {}),
    });
  },

  async triggerSync() {
    const response = await ehrPost('/sync', {});
    if (!response.success) throw new Error(response.error || 'Sync failed');
    return response.data;
  },

  /**
   * Lightweight per-user EHR refresh fired once after login (auto) or
   * on demand via an explicit refresh action (`force=true`). The backend
   *
   *   1. fuzzy-matches the authed user's name + email against the
   *      cached providers list,
   *   2. fetches today's appointments for the matched provider, and
   *   3. enriches the resulting patients' demographics.
   *
   * Throttled server-side to once per 15 minutes per user, so calling
   * this on login and on every home-screen visit is safe — repeat calls
   * within the window are returned as `{deduped: true}` after a single
   * Firestore read.
   *
   * The route always returns 200 even on failure (the response body
   * carries the status), so wrapping in try/catch only protects against
   * network-level errors.
   */
  async syncUserOnLogin({force = false} = {}) {
    const path = force ? '/sync/me?force=true' : '/sync/me';
    try {
      const response = await ehrPost(path, {}, [200]);
      if (!response?.success) {
        return {status: 'failed', reason: response?.error || 'unknown'};
      }
      return response.data || null;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('syncUserOnLogin failed', err);
      }
      return null;
    }
  },

  /**
   * Fire-and-forget request that asks the backend to refresh the encounter
   * cache for a single EHR-linked patient. Called when a clinician starts
   * a live encounter or scribe session, opens a patient detail view, or
   * otherwise indicates intent to work with this patient — so that by the
   * time they're ready to push notes (or just look up the right encounter
   * row), the cached list is up to date, including same-day visits the
   * nightly cron couldn't have known about.
   *
   * Two layers of dedup keep this safe even under impatient navigation:
   *   1. This function's in-memory map skips repeats within the same SPA
   *      session for `ENCOUNTER_SYNC_DEDUP_MS`. Cheap, prevents network
   *      round-trips that we already know the backend will skip.
   *   2. The backend transactionally guards against cross-tab / cross-
   *      device dupes via a Firestore status doc, so even the rare case
   *      where this map is bypassed (page reload, second tab) collapses
   *      down to a single Ayden call.
   *
   * Pass `force: true` to bypass the in-memory map (e.g. an explicit
   * "refresh encounters" button — backend Firestore dedup still applies).
   * Resolves silently on any error so callers can `await` it without
   * wrapping in their own try/catch.
   */
  async syncPatientEncounters(patientId: any, {force = false} = {}) {
    if (!patientId) return null;
    if (!force) {
      const lastFiredAt = lastEncounterSyncByPatientId.get(patientId);
      if (lastFiredAt && Date.now() - lastFiredAt < ENCOUNTER_SYNC_DEDUP_MS) {
        return {__deduped: true};
      }
    }
    // Mark before the request so a slow-replying backend doesn't let two
    // overlapping calls slip through during the open-window race.
    lastEncounterSyncByPatientId.set(patientId, Date.now());
    try {
      return await ehrPost(`/patients/${patientId}/encounters/sync`, {}, [202]);
    } catch (err) {
      // Failed call shouldn't poison the dedup map — clear the entry so
      // the next mount can retry instead of waiting 5 minutes.
      lastEncounterSyncByPatientId.delete(patientId);
      if (process.env.NODE_ENV === 'development') {
        console.warn('syncPatientEncounters failed', err);
      }
      return null;
    }
  },

  /**
   * Subscribe to the per-patient encounter-sync status doc the backend
   * writes inside `syncEncountersForPatient`. Resolves to a callback-style
   * unsubscribe so callers can hook this into a `useEffect` cleanup.
   *
   * The doc lives at `EhrCache/{companyId}/encounter_sync_status/{patientId}`.
   * Clients receive the latest snapshot whenever the backend flips it
   * between `syncing` / `completed` / `failed`, which the EHR push view
   * uses to render an "Encounters syncing — about 90s" banner without
   * polling. Returns a no-op unsubscribe when the inputs are missing so
   * components can call this unconditionally during render.
   */
  subscribeToEncounterSyncStatus(
    companyId: any,
    patientId: any,
    onChange: any,
  ) {
    if (!companyId || !patientId || typeof onChange !== 'function') {
      return () => {};
    }
    const ref = doc(
      db,
      'EhrCache',
      companyId,
      'encounter_sync_status',
      patientId,
    );
    return onSnapshot(
      ref,
      snap => {
        if (!snap.exists()) {
          onChange(null);
          return;
        }
        const data = snap.data() || {};
        onChange({
          patientId: data.patient_id || patientId,
          status: data.status || null,
          startedAt: data.started_at?.toDate ? data.started_at.toDate() : null,
          completedAt: data.completed_at?.toDate
            ? data.completed_at.toDate()
            : null,
          startedBy: data.started_by || null,
          encounterCount:
            typeof data.encounter_count === 'number'
              ? data.encounter_count
              : null,
          error: data.error || null,
          attempt: typeof data.attempt === 'number' ? data.attempt : null,
          nextRetryAt: data.next_retry_at?.toDate
            ? data.next_retry_at.toDate()
            : null,
        });
      },
      err => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('encounter_sync_status subscribe failed', err);
        }
        onChange(null);
      },
    );
  },

  // ── Helpers ────────────────────────────────────────────────
  ehrPatientFullName(p: any) {
    const name = normalizePatientNameParts(p || {});
    return name.displayName || name.fullName || name.patientName || 'Unknown';
  },

  /**
   * Extracts the EHR-side patient id from any patient-like object the app
   * might hand us: a local patient with `fhir_patient_id` (sometimes stored
   * as `Patient/<id>`), an EHR-mapped patient (`ehrSourceId` /
   * `ehr_source_id`), or a hub-resolved `{ ehrPatient }` wrapper. Returns
   * `null` when there's no link, so callers can short-circuit on
   * non-EHR-backed patients without their own field-shape sniffing. Mirrors
   * the iOS resolution order so client behaviour stays consistent.
   */
  resolveEhrPatientId(patient: any) {
    if (!patient) return null;
    const direct =
      patient.ehrSourceId ||
      patient.ehr_source_id ||
      patient.ehrPatientId ||
      patient.ehr_patient_id;
    if (direct) return String(direct).split('/').pop() || null;

    const fromEhrChild = patient.ehrPatient
      ? patient.ehrPatient.ehrSourceId ||
        patient.ehrPatient.ehr_source_id ||
        patient.ehrPatient.id
      : null;
    if (fromEhrChild) return String(fromEhrChild).split('/').pop() || null;

    const linked =
      patient.fhir_patient_id || patient.localPatient?.fhir_patient_id;
    if (linked) return String(linked).split('/').pop() || null;

    return null;
  },
};

// ── EHR Note Template → Scribe Template Conversion ──────────────
// Mirrors iOS EHRNoteTemplate.toScribeTemplate()

function normalizeEHRTemplate(t: any) {
  return {
    ...t,
    writableSections: (t.sections || [])
      .filter((s: any) => s.writable)
      .sort((a: any, b: any) => a.order - b.order),
    readOnlySections: (t.sections || [])
      .filter((s: any) => !s.writable)
      .sort((a: any, b: any) => a.order - b.order),
    sortedSections: [...(t.sections || [])].sort((a, b) => a.order - b.order),
  };
}

export function ehrTemplateToScribeTemplate(ehrTemplate: any) {
  const sections = (
    ehrTemplate.sortedSections ||
    ehrTemplate.sections ||
    []
  ).map((s: any, idx: number) => ({
    name: s.display_name || s.key,
    prompt:
      s.ai_prompt ||
      `Display ${s.display_name || s.key} from the patient's EHR record.`,
    ehr_key: s.key,
    writable: s.writable,
    required: s.required,
    // Fall back to the section's position when the EHR config omits an explicit
    // order — otherwise the note view can't sort these sections and renders
    // them in the backend's (arbitrary) generation order.
    order: typeof s.order === 'number' ? s.order : idx,
    non_writable_reason: s.non_writable_reason || null,
  }));

  return {
    id: `ehr_${ehrTemplate.id || ehrTemplate.name || 'unknown'}`,
    name: ehrTemplate.name,
    description: ehrTemplate.description || 'EHR note template',
    sections,
    isDynamic: false,
    isEHR: true,
    ehrTemplateId: ehrTemplate.id,
    isDefault: ehrTemplate.is_default || false,
    is_default: ehrTemplate.is_default,
    company_id: ehrTemplate.company_id,
    ehr_type: ehrTemplate.ehr_type,
  };
}

// ── Build Unified Patient List ──────────────────────────────────
// Mirrors iOS PatientViewModel.rebuildUnifiedPatients()

function normalizeName(name: any) {
  return (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

const hasPatientValue = (value: any) => String(value ?? '').trim().length > 0;

const firstPatientValue = (...values: any[]) =>
  values.find(hasPatientValue) || '';

function normalizeMrnKey(value: any) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function dateToMergeKey(date: Date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeDobKey(value: any) {
  if (!value) return '';

  const dateValue =
    typeof value?.toDate === 'function'
      ? value.toDate()
      : typeof value?.seconds === 'number'
        ? new Date(value.seconds * 1000)
        : value;

  if (dateValue instanceof Date) return dateToMergeKey(dateValue);

  const raw = String(dateValue || '').trim();
  if (!raw) return '';

  const ymd = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (ymd) {
    return `${ymd[1]}-${ymd[2].padStart(2, '0')}-${ymd[3].padStart(2, '0')}`;
  }

  const mdy = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (mdy) {
    return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? '' : dateToMergeKey(parsed);
}

function patientDob(patient: any, source: 'local' | 'ehr') {
  return source === 'local'
    ? firstPatientValue(
        patient?.date_of_birth,
        patient?.dateOfBirth,
        patient?.dob,
        patient?.birthDate,
      )
    : firstPatientValue(
        patient?.dob,
        patient?.dateOfBirth,
        patient?.date_of_birth,
        patient?.birthDate,
      );
}

function patientMrn(patient: any) {
  return firstPatientValue(
    patient?.mrn,
    patient?.medicalRecordNumber,
    patient?.accountNo,
    patient?.account_no,
  );
}

function linkedEhrIdForLocalPatient(patient: any) {
  return firstPatientValue(
    patient?.fhir_patient_id,
    patient?.ehr_patient_id,
    patient?.ehrPatientId,
    patient?._ehrPatientId,
    patient?._linkedEhrId,
  );
}

function demographicMergeKey(
  patient: any,
  source: 'local' | 'ehr',
  name = normalizePatientNameParts(patient),
) {
  const nameKey = normalizeName(
    name.fullName || name.displayName || name.patientName,
  );
  const dobKey = normalizeDobKey(patientDob(patient, source));
  const mrnKey = normalizeMrnKey(patientMrn(patient));
  if (!nameKey || !dobKey || !mrnKey) return '';
  return `${nameKey}|${dobKey}|${mrnKey}`;
}

function patientDetailScore(p: any) {
  return [
    p?.first_name || p?.firstName,
    p?.middle_name || p?.middleName,
    p?.last_name || p?.lastName,
    patientDob(p, 'local') || patientDob(p, 'ehr'),
    patientMrn(p),
    p?.preferred_language || p?.preferredLanguage || p?.language,
    p?.phone_number || p?.phoneNumber || p?.phone,
    p?.email,
  ].filter(value => String(value ?? '').trim().length > 0).length;
}

function shouldPreferLocalPatient(candidate: any, current: any) {
  if (!current) return true;
  const candidateHasLink = Boolean(linkedEhrIdForLocalPatient(candidate));
  const currentHasLink = Boolean(linkedEhrIdForLocalPatient(current));
  if (candidateHasLink !== currentHasLink) return candidateHasLink;
  return patientDetailScore(candidate) > patientDetailScore(current);
}

function shouldPreferEhrPatient(
  candidate: any,
  current: any,
  localPatient: any = null,
) {
  if (!current) return true;
  if (localPatient) {
    const candidateMatchesLink = localEhrLinkMatches(localPatient, candidate);
    const currentMatchesLink = localEhrLinkMatches(localPatient, current);
    if (candidateMatchesLink !== currentMatchesLink)
      return candidateMatchesLink;
  }
  return patientDetailScore(candidate) > patientDetailScore(current);
}

function patientSortName(name: any) {
  return (name.lastName || name.firstName || '').toLowerCase();
}

function buildLocalPatientRow(p: any, name = normalizePatientNameParts(p)) {
  return {
    id: `local_${p.id}`,
    localPatient: p,
    ehrPatient: null,
    matchKind: 'localOnly',
    displayName: name.displayName || name.fullName || 'Unknown',
    sortName: patientSortName(name),
  };
}

function normalizeEhrPatientForRow(
  ehrP: any,
  name = normalizePatientNameParts(ehrP),
) {
  return {
    ...ehrP,
    firstName: name.firstName || null,
    middleName: name.middleName || null,
    lastName: name.lastName || null,
    displayName: name.displayName || null,
    fullName: name.fullName || null,
    patientName: name.patientName || null,
  };
}

function buildEhrPatientRow(ehrP: any, name = normalizePatientNameParts(ehrP)) {
  const normalizedEhrPatient = normalizeEhrPatientForRow(ehrP, name);
  const uiIdentity = getStableReadonlyIdentity(normalizedEhrPatient);
  return {
    id: `readonly_${uiIdentity}`,
    localPatient: null,
    ehrPatient: normalizedEhrPatient,
    matchKind: 'ehrOnly',
    displayName:
      name.displayName || name.fullName || name.patientName || 'Unknown',
    sortName: patientSortName(name),
  };
}

function localEhrLinkMatches(localPatient: any, ehrPatient: any) {
  const linkedId = normalizeLinkedEhrId(
    linkedEhrIdForLocalPatient(localPatient),
  );
  const ehrId = getEhrPatientId(ehrPatient);
  if (!linkedId || !ehrId) return false;
  return linkedId === ehrId || linkedId.endsWith(`/${ehrId}`);
}

function mergeLocalRowWithEhr(row: any, ehrPatient: any, ehrName: any) {
  const normalizedEhrPatient = normalizeEhrPatientForRow(ehrPatient, ehrName);
  return {
    ...row,
    ehrPatient: normalizedEhrPatient,
    matchKind: localEhrLinkMatches(row.localPatient, normalizedEhrPatient)
      ? 'mergedByLinkedEhr'
      : 'mergedByExactName',
    displayName:
      row.displayName ||
      ehrName.displayName ||
      ehrName.fullName ||
      ehrName.patientName ||
      'Unknown',
    sortName: row.sortName || patientSortName(ehrName),
  };
}

function titleCaseWords(value: any) {
  return String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function buildCachedPatientSearchValues(searchQuery: any) {
  const queryVariants = buildPatientSearchQueries(searchQuery);
  const normalized = normalizePatientSearchText(searchQuery);
  const exactValues = [
    ...queryVariants,
    normalized,
    String(searchQuery || '').replace(/\D/g, ''),
  ].filter(value => String(value || '').trim().length > 0);

  const namePrefixes = [
    ...queryVariants,
    ...queryVariants.map(titleCaseWords),
  ].filter(value => normalizePatientSearchText(value).length >= 2);

  return {
    exactValues: [...new Set(exactValues.map(value => String(value).trim()))],
    namePrefixes: [...new Set(namePrefixes.map(value => String(value).trim()))],
  };
}

export function buildUnifiedPatientList(
  localPatients: any[] = [],
  ehrPatients: any[] = [],
) {
  // The backend can currently return the same person as separate Local,
  // Linked, and EHR records. Collapse only exact demographic duplicates so
  // name-only matches remain separate while hydrated planner rows see one
  // patient item carrying the local and EHR sides.
  const result: any[] = [];
  const localGroupByMergeKey = new Map();
  const ehrOnlyGroupByMergeKey = new Map();

  for (const p of localPatients) {
    const name = normalizePatientNameParts(p);
    const mergeKey = demographicMergeKey(p, 'local', name);
    if (!mergeKey) {
      result.push(buildLocalPatientRow(p, name));
      continue;
    }

    const existing = localGroupByMergeKey.get(mergeKey);
    if (!existing) {
      const row = buildLocalPatientRow(p, name);
      localGroupByMergeKey.set(mergeKey, {row, resultIndex: result.length});
      result.push(row);
      continue;
    }

    if (shouldPreferLocalPatient(p, existing.row.localPatient)) {
      const row = buildLocalPatientRow(p, name);
      existing.row = row;
      result[existing.resultIndex] = row;
    }
  }

  for (const ehrP of ehrPatients) {
    const name = normalizePatientNameParts(ehrP);
    const mergeKey = demographicMergeKey(ehrP, 'ehr', name);
    const localGroup = mergeKey ? localGroupByMergeKey.get(mergeKey) : null;

    if (localGroup) {
      if (
        shouldPreferEhrPatient(
          ehrP,
          localGroup.row.ehrPatient,
          localGroup.row.localPatient,
        )
      ) {
        const row = mergeLocalRowWithEhr(localGroup.row, ehrP, name);
        localGroup.row = row;
        result[localGroup.resultIndex] = row;
      }
      continue;
    }

    if (mergeKey) {
      const existing = ehrOnlyGroupByMergeKey.get(mergeKey);
      if (existing) {
        if (shouldPreferEhrPatient(ehrP, existing.row.ehrPatient)) {
          const row = buildEhrPatientRow(ehrP, name);
          existing.row = row;
          result[existing.resultIndex] = row;
        }
        continue;
      }

      const row = buildEhrPatientRow(ehrP, name);
      ehrOnlyGroupByMergeKey.set(mergeKey, {
        row,
        resultIndex: result.length,
      });
      result.push(row);
      continue;
    }

    result.push(buildEhrPatientRow(ehrP, name));
  }

  return result;
}

// ── Extract ICD-10 codes from assessment text ───────────────────
export function extractICD10Codes(assessmentText: any) {
  if (!assessmentText) return null;
  // `\.\w+` (not `\.\d{1,4}`) so 7th-character codes like S82.432A and
  // X-placeholder codes like T36.0X1A are extracted and written to the EHR.
  const matches = assessmentText.match(/[A-Z]\d{2}(?:\.\w+)?/g);
  return matches?.length ? [...new Set(matches)] : null;
}

function normalizeIcd10Payload(icd10: any) {
  if (icd10 == null) return undefined;

  const values = Array.isArray(icd10) ? icd10 : [icd10];
  const normalized = values
    .map(value => String(value || '').trim())
    .filter(Boolean);

  return normalized.length ? [...new Set(normalized)] : undefined;
}

function normalizeLinkedEhrId(id: any) {
  if (!id) return null;
  const trimmed = String(id).trim();
  return trimmed || null;
}

function getEhrPatientId(ehrPatient: any) {
  return normalizeLinkedEhrId(
    ehrPatient?.ehrSourceId || ehrPatient?.ehr_source_id || ehrPatient?.id,
  );
}

function getStableReadonlyIdentity(ehrPatient: any) {
  const explicitId = getEhrPatientId(ehrPatient);
  if (explicitId) return explicitId;

  const fingerprint = [
    normalizeName(
      `${ehrPatient?.firstName || ''}${ehrPatient?.lastName || ''}`,
    ),
    normalizeLinkedEhrId(ehrPatient?.mrn),
    normalizeLinkedEhrId(ehrPatient?.dob),
  ]
    .filter(Boolean)
    .join('_');

  return fingerprint || 'unknown';
}

function mapEHRPatient(documentId: any, data: any) {
  const ehrSourceId = normalizeLinkedEhrId(
    data.ehr_source_id || data.ehrSourceId,
  );
  const rawMrn = normalizePatientMrn(data.mrn) || null;
  const rawAccountNo = normalizePatientMrn(data.accountNo) || null;
  const name = normalizePatientNameParts({
    firstName: data.firstName || data.first_name,
    middleName: data.middleName || data.middle_name,
    lastName: data.lastName || data.last_name,
    fullName: data.fullName || data.full_name,
    displayName: data.displayName,
    patientName: data.patientName || data.patient_name,
  });
  return {
    id: ehrSourceId || documentId,
    cacheDocumentId: documentId,
    ehrSourceId,
    ehrType: data.ehr_type || null,
    firstName: name.firstName || null,
    lastName: name.lastName || null,
    middleName: name.middleName || null,
    displayName: name.displayName || null,
    fullName: name.fullName || null,
    patientName: name.patientName || null,
    dob: data.dob || null,
    gender: data.gender || null,
    email: data.email || null,
    phone: data.phone || null,
    // Many ECW tenants leave `mrn` empty and use `accountNo` as the
    // patient-facing identifier. Surface whichever is populated as `mrn`
    // so UI code that only reads `.mrn` keeps working; keep `accountNo`
    // available separately for anyone who needs the distinction.
    mrn: rawMrn || rawAccountNo,
    accountNo: rawAccountNo,
    language: data.language || null,
    pcp: data.pcp || null,
    pcpId: data.pcpId || null,
  };
}

function chunkArray<T>(arr: readonly T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}
